/** CRM / platform permission helpers (effective_permissions + super_admin bypass). */

export const CRM_ROLES = ['super_admin', 'sales_executive', 'team_lead', 'regional_manager', 'crm_user'];

/** School system slugs must never be treated as CRM/platform (mirrors backend SCHOOL_SYSTEM_SLUGS). */
export const SCHOOL_SYSTEM_ROLES = [
  'school_admin', 'principal', 'teacher', 'staff', 'student', 'parent', 'receptionist',
];

export const PLATFORM_PERMISSION_KEYS = [
  'crm_dashboard',
  'crm_inquiries',
  'crm_inquiries_assign',
  'crm_inquiries_delete',
  'crm_employees',
  'crm_attendance',
  'crm_attendance_mark',
  'crm_mail',
  'crm_tickets',
  'crm_tickets_manage',
  'crm_tickets_assign',
  'crm_tickets_reports',
  'crm_payroll',
  'crm_reports',
  'crm_plans',
  'crm_assigned_feedback',
  'manage_schools',
  'manage_bin',
  'manage_platform_feedback',
  'manage_subscription_plans',
  'manage_subscriptions',
  'manage_platform_settings',
  'manage_platform_roles',
];

/** Legacy key → new module.action keys (any match grants legacy). */
export const CRM_LEGACY_ALIASES = {
  crm_dashboard: ['dashboard.read'],
  crm_inquiries: ['inquiries.read', 'inquiries.create', 'inquiries.update'],
  crm_inquiries_assign: ['inquiries.assign'],
  crm_inquiries_delete: ['inquiries.delete'],
  crm_employees: ['employees.manage', 'employees.create', 'employees.update', 'employees.delete'],
  crm_attendance: ['attendance.read'],
  crm_attendance_mark: ['attendance.create', 'attendance.update'],
  crm_mail: ['mail.read', 'mail.create'],
  crm_tickets: ['tickets.read', 'tickets.create'],
  crm_tickets_manage: ['tickets.manage', 'tickets.update'],
  crm_tickets_assign: ['tickets.assign'],
  crm_tickets_reports: ['tickets.export', 'reports.read'],
  crm_payroll: ['payroll.read', 'payroll.manage', 'payroll.create', 'payroll.update', 'payroll.approve'],
  crm_reports: ['reports.read', 'reports.export'],
  crm_plans: ['plans.read'],
  crm_assigned_feedback: ['feedback.read', 'feedback.update'],
  manage_schools: ['schools.manage', 'schools.read'],
  manage_bin: ['bin.manage', 'bin.read'],
  manage_platform_feedback: ['platform_feedback.manage', 'platform_feedback.read'],
  manage_subscription_plans: ['subscription_plans.manage', 'subscription_plans.read'],
  manage_subscriptions: ['subscriptions.manage', 'subscriptions.read'],
  manage_platform_settings: ['platform_settings.manage', 'platform_settings.read'],
  manage_platform_roles: ['platform_roles.manage', 'platform_roles.read', 'roles.manage'],
};

const PLATFORM_PERM_SET = new Set(PLATFORM_PERMISSION_KEYS);

function roleSlugOf(userOrSlug) {
  if (userOrSlug == null) return '';
  if (typeof userOrSlug === 'string') return String(userOrSlug).toLowerCase().trim();
  return String(userOrSlug.role_slug ?? '').toLowerCase().trim();
}

function userOf(userOrSlug) {
  if (userOrSlug != null && typeof userOrSlug === 'object') return userOrSlug;
  return null;
}

export function isSuperAdmin(userOrSlug) {
  return roleSlugOf(userOrSlug) === 'super_admin';
}

export function isHod(userOrSlug) {
  const user = userOf(userOrSlug);
  return !!(user && user.is_hod);
}

/** Check module.action against effective_permissions (and legacy aliases). */
export function hasPermission(userOrSlug, module, action) {
  return hasPermissionKey(userOrSlug, `${String(module).toLowerCase()}.${String(action).toLowerCase()}`);
}

export function hasPermissionKey(userOrSlug, key) {
  if (isSuperAdmin(userOrSlug)) return true;
  const user = userOf(userOrSlug);
  const perms = user?.effective_permissions;
  const keyNorm = String(key || '').toLowerCase().trim();
  if (!keyNorm) return false;

  if (Array.isArray(perms)) {
    if (perms.includes(keyNorm)) return true;
    // legacy key checked against new keys in effective set
    const mapped = CRM_LEGACY_ALIASES[keyNorm];
    if (mapped && mapped.some((k) => perms.includes(k))) return true;
    // new key → also grant if any legacy that maps to it is present
    for (const [legacy, news] of Object.entries(CRM_LEGACY_ALIASES)) {
      if (news.includes(keyNorm) && perms.includes(legacy)) return true;
    }
    return false;
  }

  // Legacy call sites that only pass role slug — fall back to seed-era slug rules
  const role = roleSlugOf(userOrSlug);
  const slugDefaults = {
    sales_executive: [
      'crm_dashboard', 'crm_inquiries', 'crm_attendance', 'crm_mail', 'crm_tickets', 'crm_plans', 'crm_assigned_feedback',
    ],
    team_lead: [
      'crm_dashboard', 'crm_inquiries', 'crm_attendance', 'crm_attendance_mark', 'crm_mail',
      'crm_tickets', 'crm_tickets_manage', 'crm_tickets_reports', 'crm_plans', 'crm_assigned_feedback',
    ],
    regional_manager: [
      'crm_dashboard', 'crm_inquiries', 'crm_attendance', 'crm_attendance_mark', 'crm_mail',
      'crm_tickets', 'crm_tickets_manage', 'crm_tickets_reports', 'crm_plans', 'crm_assigned_feedback', 'crm_reports',
    ],
  };
  const defaults = slugDefaults[role] || [];
  if (defaults.includes(keyNorm)) return true;
  const mapped = CRM_LEGACY_ALIASES[keyNorm];
  if (mapped) {
    // no effective perms — only legacy slug defaults apply
    return false;
  }
  return false;
}

export function hasPlatformPermission(userOrSlug, key) {
  return hasPermissionKey(userOrSlug, key);
}

export function isCrmRole(userOrSlug) {
  const role = roleSlugOf(userOrSlug);
  if (SCHOOL_SYSTEM_ROLES.includes(role)) return false;
  if (CRM_ROLES.includes(role)) return true;
  const user = userOf(userOrSlug);
  if (user?.role_scope === 'platform') return true;
  if (user?.crm_employee_id || user?.crm_role_id) return true;
  if (Array.isArray(user?.effective_permissions)) {
    return user.effective_permissions.some((p) => {
      const key = String(p);
      return key.startsWith('crm_') || key.includes('.') || PLATFORM_PERM_SET.has(key);
    });
  }
  return false;
}

export function isCrmEmployeeRole(userOrSlug) {
  const role = roleSlugOf(userOrSlug);
  if (['sales_executive', 'team_lead', 'regional_manager', 'crm_user'].includes(role)) return true;
  if (role === 'super_admin' || role === '') return false;
  const user = userOf(userOrSlug);
  return user?.role_scope === 'platform' || !!(user?.crm_role_id);
}

export function canManageCrmEmployees(userOrSlug) {
  return hasPermissionKey(userOrSlug, 'crm_employees') || hasPermission(userOrSlug, 'employees', 'manage');
}

export function canManageDepartments(userOrSlug) {
  return isSuperAdmin(userOrSlug) || hasPermission(userOrSlug, 'departments', 'manage') || hasPermission(userOrSlug, 'departments', 'read');
}

export function canManageCrmRoles(userOrSlug) {
  return isSuperAdmin(userOrSlug) || isHod(userOrSlug)
    || hasPermission(userOrSlug, 'roles', 'manage') || hasPermission(userOrSlug, 'roles', 'read');
}

export function canManageCrmSalaries(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_payroll');
}

export function canViewCrmReports(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_reports');
}

export function canMarkCrmAttendance(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_attendance_mark');
}

export function canViewCrmAttendance(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_attendance') || canMarkCrmAttendance(userOrSlug);
}

export function canAssignCrmInquiries(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_inquiries_assign');
}

export function canDeleteCrmInquiries(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_inquiries_delete');
}

export function canAccessCrmInquiries(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_inquiries');
}

export function canViewCrmPlans(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_plans');
}

export function canManageAssignedFeedback(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_assigned_feedback') || isCrmEmployeeRole(userOrSlug);
}

export function canAccessCrmChat(user) {
  return isCrmRole(user) && !user?.school_observer_mode;
}

export function canAccessCrmMail(user) {
  return hasPlatformPermission(user, 'crm_mail') && !user?.school_observer_mode;
}

export function canAccessSchoolChat(user) {
  return !isCrmRole(user) || !!user?.school_observer_mode;
}

export function canAccessCrmTickets(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_tickets');
}

export function canCreateCrmTickets(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_tickets');
}

export function canManageCrmTickets(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_tickets_manage');
}

export function canAssignCrmTickets(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_tickets_assign');
}

export function canViewCrmTicketReports(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_tickets_reports');
}

export function canManageSchools(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_schools');
}

export function canManageBin(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_bin');
}

export function canManagePlatformFeedback(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_platform_feedback');
}

export function canManageSubscriptionPlans(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_subscription_plans');
}

export function canManageSubscriptions(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_subscriptions');
}

export function canManagePlatformSettings(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_platform_settings');
}

export function canManagePlatformRoles(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'manage_platform_roles');
}

export function canAccessCrmDashboard(userOrSlug) {
  return hasPlatformPermission(userOrSlug, 'crm_dashboard') || isCrmRole(userOrSlug);
}
